import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

import { CollectionDynamicVariants } from './CollectionDynamic.types';
import { type LayoutConfig } from '../ThemeRegistry/mixins/generateGridStyles';
const defaultProps: ComponentsProps['CollectionDynamic'] = {};

export const layoutConfig: LayoutConfig = {
  [CollectionDynamicVariants.onePerRow]: {
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
    xl: 1,
    xxl: 1
  },
  [CollectionDynamicVariants.twoPerRow]: {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 2,
    xl: 2,
    xxl: 2
  },
  [CollectionDynamicVariants.threePerRow]: {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 3,
    xl: 3,
    xxl: 3
  },
  [CollectionDynamicVariants.fourPerRow]: {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 4,
    xxl: 4
  },
  [CollectionDynamicVariants.fivePerRow]: {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 5,
    xxl: 5
  }
};

const styleOverrides: ComponentsOverrides<Theme>['CollectionDynamic'] = {
  root: ({ theme, ownerState }) => ({
    ...theme.mixins.applyBackgroundColor({ ownerState, theme }),

    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    gap: 'var(--grid-gap)'
  }),

  introTextGrid: ({ theme, ownerState }) => ({
    '& [class*=Text-root]': {
      marginBottom: 0,

      gridColumn: 'full-start/full-end'
    }
  }),

  contentGrid: ({ theme, ownerState }) => ({
    gap: 'var(--grid-gap)',
    gridTemplateRows: 'auto !important'
  }),

  filtersOuterWrap: ({ theme, ownerState }) => ({
    gridColumnStart: 'start !important',
    gridColumnEnd: 'end !important',
    display: 'flex',
    flexDirection: 'column',
    gridRow: 1,
    flexWrap: 'wrap',

    [theme.breakpoints.up('md')]: {
      'flexDirection': 'row',
      'gridTemplateColumns': 'repeat(4, 1fr)',

      '& > *:first-child': {
        gridColumn: 'span 1'
      },
      '& > *:not(:first-child)': {
        gridColumn: 'span 3'
      }
    }
  }),

  searchBoxWrap: ({ theme }) => ({
    ...theme.mixins.applyBackgroundColor({ ownerState: { backgroundColor: 'white' }, theme }),

    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.spacing(10),

    input: { ...theme.typography.body1, padding: theme.spacing(1.5, 3) },

    svg: {
      position: 'absolute',
      width: theme.spacing(2),
      height: theme.spacing(2),
      right: theme.spacing(3)
    }
  }),

  currentRefinementsWrap: ({ theme, ownerState }) => ({
    gridColumn: 'start/end',
    gap: 'inherit',
    gridRow: 2,
    marginTop: theme.spacing(3),

    [theme.breakpoints.up('md')]: {
      gridRow: 3,
      gridColumn: 'start/end'
    }
  }),

  filtersWrap: ({ theme, ownerState }) => ({
    'display': 'flex',
    'gap': theme.spacing(2),
    'flexDirection': 'column',
    'marginTop': 'var(--grid-gap-double)',
    'marginBottom': 'var(--grid-gap-double)',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gridColumn: 'span 3'
    },

    ...(ownerState?.filtersPlacement === 'left'
      ? {
          position: 'sticky',
          top: 'var(--grid-gap-double)',
          flexDirection: 'column'
        }
      : {}),

    '.MuiFormControl-root': {
      'flex': 1,
      'display': 'flex',
      'justifyContent': 'center',
      'zIndex': 1,

      '.MuiInput-underline': {
        'margin': '0px',

        '&::before': {
          color: 'var(--mui-palette-schemes-navyBlue-primary-light)',
          opacity: '0.7'
        },

        '.MuiInputBase-input': {
          padding: theme.spacing(2, 3)
        },

        'svg': {
          position: 'absolute',
          width: theme.spacing(2),
          height: theme.spacing(2),
          right: theme.spacing(3)
        }
      }
    }
  }),

  resultsWrap: ({ theme, ownerState }) => ({
    gridColumn: 'start/end',
    gap: 'inherit',
    gridRow: 2,

    [theme.containerBreakpoints.up('md')]: {
      ...(ownerState?.filtersPlacement === 'left' && {
        gridRow: 1,
        gridColumn: 'four-start/end'
        // ...theme.mixins.applyBackgroundColor({ ownerState, backgroundColor: 'white' })
      })
    }
  }),

  itemsGrid: ({ theme, ownerState }) => ({
    'gap': 'var(--grid-gap-quad) var(--grid-gap-double)',

    [theme.containerBreakpoints.up('xl')]: {
      gap: 'var(--grid-gap-quad)'
    },

    'position': 'relative',

    ...(ownerState &&
      theme.mixins.generateGridStyles({
        theme,
        layoutConfig,
        variant: ownerState.variant,
        defaultVariant: 'default'
      })),

    '& [class*=ais-Hit]': {
      display: 'contents'
    }
  }),

  paginationWrap: ({ theme }) => ({
    gridColumn: '1 / -1',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    ul: {
      '& > :first-child, & > :last-child': {
        svg: {
          width: theme.spacing(2),
          height: theme.spacing(2)
        }
      },

      'button': {
        'color': 'var(--mui-palette-schemes-navyBlue-primary-main)',
        'margin': theme.spacing(0, 0.5),
        '&:hover': {
          background: 'var(--mui-palette-schemes-navyBlue-primary-light)'
        }
      },

      'button.Mui-selected': {
        'background': 'var(--mui-palette-schemes-blue-primary-light)',
        '&:hover': {
          background: 'var(--mui-palette-schemes-blue-primary-light)'
        }
      }
    }
  }),

  viewAllResultsWrap: ({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end'
  }),

  actionsWrap: ({ theme, ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'var(--grid-gap-double)',
    gap: 'var(--grid-gap)',

    [theme.containerBreakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: 'var(--grid-gap-double)'
    }
  })
};

const createVariants = (_theme: Theme): ComponentsVariants['CollectionDynamic'] => [];

const collectionDynamicTheme = (theme: Theme): ThemeOptions => ({
  components: {
    CollectionDynamic: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default collectionDynamicTheme;
